import {
  FileTextOutlined,
  InboxOutlined,
  UserAddOutlined
} from "@ant-design/icons"
import { Alert, Form, Modal, Select, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useBulkCommunicate } from "../../../Communication/bulk/use-bulk-communicate.tsx"
import { BulkActionBar } from "../../../../components/Table/BulkActionBar.tsx"
import {
  type ClosingStatus,
  UnpaidInvoicesDialog
} from "../../components/UnpaidInvoicesDialog.tsx"
import { PROJECT_STATUSES } from "../../constants/project-statuses.ts"
import type { ProjectBulkResult } from "../../redux/projects.types.ts"

const { Text } = Typography

export type ProjectBulkAction = "change-status" | "assign-pm" | "archive"

export interface ProjectBulkActionParams {
  action: ProjectBulkAction
  ids: string[]
  status?: number
  projectManagerId?: string
  /** The projects the user agreed to close with unpaid invoices. */
  acknowledgeUnpaidInvoicesFor?: string[]
}

export interface UnpaidProjectInfo {
  identifier: string | null
  count: number
}

const isClosingStatus = (status?: number): status is ClosingStatus =>
  status === PROJECT_STATUSES.COMPLETED || status === PROJECT_STATUSES.CANCELLED

interface UnpaidQuestion {
  status: ClosingStatus
  ids: string[]
  unpaidIds: string[]
}

interface Option<V> {
  label: string
  value: V
}

interface ProjectsBulkActionBarProps {
  selectedIds: string[]
  // A selection survives loading more rows, so a failure can name a project
  // whose row is not currently mounted — fall back to the id when it is absent.
  projectNames: Record<string, string>
  /** Rows still owed money, keyed by id; a row owed nothing is absent. */
  unpaidInvoices?: Record<string, UnpaidProjectInfo>
  statusOptions: Option<number>[]
  projectManagerOptions: Option<string>[]
  editAllowed?: boolean
  assignPmAllowed?: boolean
  archiveAllowed?: boolean
  onClear: () => void
  onAction: (params: ProjectBulkActionParams) => Promise<ProjectBulkResult>
  /**
   * The bar outlives the selection — it keeps reporting the outcome after an
   * action clears it — so anything anchored to the same corner follows this
   * rather than the selection.
   */
  onVisibleChange?: (visible: boolean) => void
}

type BulkFormValues = { status?: number; projectManagerId?: string }

export function ProjectsBulkResult({
  result,
  projectNames,
  skippedCount = 0
}: {
  result: ProjectBulkResult
  projectNames: Record<string, string>
  skippedCount?: number
}) {
  const { t } = useTranslation()

  return (
    <div>
      <Text strong>
        {t("projectBulk.result.summary", {
          succeeded: result.succeeded.length,
          requested: result.requested
        })}
      </Text>
      {skippedCount ? (
        <div>
          <Text>{t("projectUnpaid.bulkSkipped", { count: skippedCount })}</Text>
        </div>
      ) : null}
      {/* Bounded because the bar is pinned to the bottom: an unbounded list
          grows upwards over the table rather than overflowing down. */}
      {result.failed.length ? (
        <ul
          data-testid="projects-bulk-result-failures"
          // Nothing inside takes focus, so the region carries it — otherwise
          // the refusals below the cap are unreachable without a pointer.
          tabIndex={0}
          aria-label={t("projectBulk.result.failuresLabel", {
            count: result.failed.length
          })}
          className="mb-0 mt-2 max-h-[30vh] list-disc overflow-y-auto pl-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2A7D8C]"
        >
          {result.failed.map((failure) => (
            <li key={failure.id}>
              {t("projectBulk.result.failure", {
                project: projectNames[failure.id] || failure.id,
                reason: failure.message
              })}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export function ProjectsBulkActionBar({
  selectedIds,
  projectNames,
  unpaidInvoices = {},
  statusOptions,
  projectManagerOptions,
  editAllowed,
  assignPmAllowed,
  archiveAllowed,
  onClear,
  onAction,
  onVisibleChange
}: ProjectsBulkActionBarProps) {
  const { t } = useTranslation()
  const communicate = useBulkCommunicate("project", selectedIds)
  const [form] = Form.useForm<BulkFormValues>()
  const [activeAction, setActiveAction] = useState<ProjectBulkAction | null>(
    null
  )
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<ProjectBulkResult | null>(null)
  const [requestFailed, setRequestFailed] = useState(false)
  const [unpaidQuestion, setUnpaidQuestion] = useState<UnpaidQuestion | null>(
    null
  )
  const [skippedCount, setSkippedCount] = useState(0)
  // Projects the server refused for unpaid invoices that their rows did not
  // show. The refetch after the batch reloads only the first page, so a
  // refused project further down may no longer have a row to read; without
  // this, running Change Status again would never ask and be refused again.
  const [refusedUnpaid, setRefusedUnpaid] = useState<
    Record<string, UnpaidProjectInfo & { name?: string }>
  >({})

  const unpaidInfo = (id: string) =>
    (unpaidInvoices[id]?.count ?? 0) > 0
      ? unpaidInvoices[id]
      : refusedUnpaid[id]

  const actionLabels = useMemo<Record<ProjectBulkAction, string>>(
    () => ({
      "change-status": t("projectBulk.actions.changeStatus"),
      "assign-pm": t("projectBulk.actions.assignPm"),
      archive: t("projectBulk.actions.archive")
    }),
    [t]
  )

  const closeModal = () => {
    if (submitting) return
    setActiveAction(null)
    form.resetFields()
  }

  const openModal = (action: ProjectBulkAction) => {
    form.resetFields()
    setActiveAction(action)
  }

  const submitAction = async () => {
    if (!activeAction) return

    let values: BulkFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    // The field's own rule covers this, but the request must not depend on the
    // form being connected in time.
    if (activeAction === "change-status" && values.status == null) return
    if (activeAction === "assign-pm" && !values.projectManagerId) return

    // Asked once for the whole selection, before anything is sent, rather
    // than letting each owed project come back refused.
    if (activeAction === "change-status" && isClosingStatus(values.status)) {
      const unpaidIds = selectedIds.filter(
        (id) => (unpaidInfo(id)?.count ?? 0) > 0
      )
      if (unpaidIds.length) {
        setActiveAction(null)
        form.resetFields()
        setUnpaidQuestion({
          status: values.status,
          ids: selectedIds,
          unpaidIds
        })
        return
      }
    }

    await runAction({
      action: activeAction,
      ids: selectedIds,
      ...(activeAction === "change-status" ? { status: values.status } : {}),
      ...(activeAction === "assign-pm"
        ? { projectManagerId: values.projectManagerId }
        : {})
    })
  }

  const runAction = async (params: ProjectBulkActionParams, skipped = 0) => {
    setSubmitting(true)
    try {
      const nextResult = await onAction(params)
      if (params.action === "change-status") {
        rememberRefusedUnpaid(nextResult)
      }
      setRequestFailed(false)
      setResult(nextResult)
      setSkippedCount(skipped)
    } catch {
      setResult(null)
      setRequestFailed(true)
    } finally {
      setActiveAction(null)
      setUnpaidQuestion(null)
      form.resetFields()
      setSubmitting(false)
    }
  }

  const rememberRefusedUnpaid = (nextResult: ProjectBulkResult) =>
    setRefusedUnpaid((previous) => {
      const next = { ...previous }
      nextResult.succeeded.forEach((id) => delete next[id])
      nextResult.failed.forEach(({ id, unpaidInvoiceCount }) => {
        if (unpaidInvoiceCount) {
          next[id] = {
            identifier: unpaidInvoices[id]?.identifier ?? null,
            count: unpaidInvoiceCount,
            name: projectNames[id] ?? previous[id]?.name
          }
        }
      })
      return next
    })

  const answerUnpaidQuestion = (skipUnpaid: boolean) => {
    if (!unpaidQuestion) return
    const { status, ids, unpaidIds } = unpaidQuestion
    void runAction(
      skipUnpaid
        ? {
            action: "change-status",
            ids: ids.filter((id) => !unpaidIds.includes(id)),
            status
          }
        : {
            action: "change-status",
            ids,
            status,
            acknowledgeUnpaidInvoicesFor: unpaidIds
          },
      skipUnpaid ? unpaidIds.length : 0
    )
  }

  const clear = () => {
    setResult(null)
    setRequestFailed(false)
    setRefusedUnpaid({})
    onClear()
  }

  const visible =
    !!selectedIds.length || !!result || requestFailed || !!unpaidQuestion

  useEffect(() => {
    onVisibleChange?.(visible)
  }, [visible, onVisibleChange])

  if (!visible) return null

  const feedback =
    requestFailed || result ? (
      <>
        {requestFailed ? (
          <Alert
            data-testid="projects-bulk-request-error"
            type="error"
            showIcon
            closable
            onClose={() => setRequestFailed(false)}
            message={t("projectBulk.result.requestFailed")}
          />
        ) : null}

        {result ? (
          <Alert
            type={result.failed.length ? "warning" : "success"}
            showIcon
            closable
            onClose={() => setResult(null)}
            message={
              <ProjectsBulkResult
                result={result}
                projectNames={projectNames}
                skippedCount={skippedCount}
              />
            }
          />
        ) : null}
      </>
    ) : null

  return (
    <>
      {communicate.wizard}
      <BulkActionBar
        data-testid="projects-bulk-action-bar"
        visible
        selectedCount={selectedIds.length}
        selectedLabel={t("projectBulk.selected", { count: selectedIds.length })}
        ariaLabel={t("projectBulk.toolbarLabel")}
        actions={[
          ...(communicate.action ? [communicate.action] : []),
          ...(editAllowed
            ? [
                {
                  key: "change-status",
                  label: actionLabels["change-status"],
                  icon: <FileTextOutlined aria-hidden />,
                  onClick: () => openModal("change-status")
                }
              ]
            : []),
          ...(assignPmAllowed
            ? [
                {
                  key: "assign-pm",
                  label: actionLabels["assign-pm"],
                  icon: <UserAddOutlined aria-hidden />,
                  onClick: () => openModal("assign-pm")
                }
              ]
            : [])
        ]}
        destructiveAction={
          archiveAllowed
            ? {
                key: "archive",
                label: actionLabels.archive,
                icon: <InboxOutlined aria-hidden />,
                onClick: () => openModal("archive")
              }
            : undefined
        }
        onClear={clear}
      >
        {/* Below the toolbar, so a result does not push the controls still in
            use further down the page. */}
        {feedback}
      </BulkActionBar>

      <Modal
        open={activeAction != null}
        title={activeAction ? actionLabels[activeAction] : undefined}
        okText={
          activeAction === "archive"
            ? t("projectBulk.actions.archive")
            : t("projectBulk.apply")
        }
        cancelText={t("projectBulk.cancel")}
        okButtonProps={{ danger: activeAction === "archive" }}
        confirmLoading={submitting}
        cancelButtonProps={{ disabled: submitting }}
        closable={!submitting}
        keyboard={!submitting}
        maskClosable={false}
        destroyOnHidden
        onOk={submitAction}
        onCancel={closeModal}
      >
        <Form form={form} layout="vertical" disabled={submitting}>
          {activeAction === "change-status" ? (
            <Form.Item<BulkFormValues>
              name="status"
              label={t("projectBulk.fields.status")}
              rules={[
                {
                  required: true,
                  message: t("projectBulk.validation.status")
                }
              ]}
            >
              <Select
                options={statusOptions}
                placeholder={t("projectBulk.placeholders.status")}
              />
            </Form.Item>
          ) : null}

          {activeAction === "assign-pm" ? (
            <>
              <Form.Item<BulkFormValues>
                name="projectManagerId"
                label={t("projectBulk.fields.projectManager")}
                rules={[
                  {
                    required: true,
                    message: t("projectBulk.validation.projectManager")
                  }
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={projectManagerOptions}
                  placeholder={t("projectBulk.placeholders.projectManager")}
                />
              </Form.Item>
              <Text type="secondary">{t("projectBulk.assignPmHint")}</Text>
            </>
          ) : null}

          {activeAction === "archive" ? (
            <Alert
              type="error"
              showIcon
              message={t("projectBulk.archiveImpact", {
                count: selectedIds.length
              })}
            />
          ) : null}
        </Form>
      </Modal>

      {unpaidQuestion ? (
        <UnpaidInvoicesDialog
          open
          mode="bulk-close"
          status={unpaidQuestion.status}
          count={unpaidQuestion.unpaidIds.length}
          selectedCount={unpaidQuestion.ids.length}
          projects={unpaidQuestion.unpaidIds.map((id) => ({
            id,
            identifier: unpaidInfo(id)?.identifier,
            name: projectNames[id] || refusedUnpaid[id]?.name || id
          }))}
          busy={submitting}
          onConfirm={() => answerUnpaidQuestion(false)}
          onSkip={() => answerUnpaidQuestion(true)}
          onClose={() => {
            if (!submitting) setUnpaidQuestion(null)
          }}
        />
      ) : null}
    </>
  )
}
